import React, { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from 'cpool-components/general/Modal';
import { Button } from 'cpool-components/input/Button';
import { AGGREGATE_NAMES, DEFAULT_USER_LANGUAGE_SETTING, DEFAULT_USER_STATUS_COLOR_MODE } from 'cpool-constants';
import { schemaUserLanguageSetting, schemaUserStatusColorMode } from 'cpool-schemas';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuSave } from 'react-icons/lu';
import { z } from 'zod';

import { DisplaySettings } from './DisplaySettings';
import { useUserData } from '../../hooks/useUserData';
import useUserSettings from '../../hooks/useUserSettings';
import { SMimeCert } from '~/components/SMimeCert/SMimeCert';
import { SmtpAccess } from '~/components/SmtpAccess/SmtpAccess';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { type SchemaUserEditForm, sMime, smtpForm } from '~/features/userManagement/schemas/SchemaUserEditForm';
import useUserInfo from '~/hooks/useUserInfo';

type Props = {
    open: boolean;
    onClose: () => void;
};

type UserSettings = {
    language: z.infer<typeof schemaUserLanguageSetting>;
    statusColorMode: z.infer<typeof schemaUserStatusColorMode>;
    smtp: NonNullable<SchemaUserEditForm['smtp']>;
    certFile: SchemaUserEditForm['certFile'];
    certPassword: SchemaUserEditForm['certPassword'];
    certFileName: SchemaUserEditForm['certFileName'];
};

const userSettingSchema = z.object({
    certFile: sMime.shape.certFile,
    certFileName: sMime.shape.certFileName,
    certPassword: sMime.shape.certPassword,
    language: schemaUserLanguageSetting,
    // form variant: an empty SMTP pair must not block saving the other settings
    smtp: smtpForm,
    statusColorMode: schemaUserStatusColorMode,
});

export const SettingsModal: React.FC<Props> = ({ onClose, open }) => {
    const { t } = useTranslation();
    const user = useUserInfo();
    const { data: userData, isFetched } = useUserData(user?.id);
    const { permissions } = useAclPermissions();
    const canChangeSmtp = permissions.commands[AGGREGATE_NAMES.USER].changeSMTP;
    const canChangePersonalSettings = permissions.commands[AGGREGATE_NAMES.USER].changePersonalSettings;
    const disabled = !canChangeSmtp && !canChangePersonalSettings;

    const form = useForm<UserSettings>({
        defaultValues: {
            certFileName: userData?.certFileName,
            language: userData?.settings.language ?? DEFAULT_USER_LANGUAGE_SETTING,
            smtp: userData?.smtp ?? { password: '', user: '' },
            statusColorMode: userData?.settings.statusColorMode ?? DEFAULT_USER_STATUS_COLOR_MODE,
        },
        resolver: zodResolver(userSettingSchema),
    });

    // the modal stays mounted so headless-ui can run its slide transition, so the form has to be
    // reset on every open — otherwise unsaved edits from a previous visit are still sitting there
    useEffect(() => {
        if (open && isFetched && userData) {
            form.reset({
                language: userData.settings.language,
                smtp: userData.smtp ?? { password: '', user: '' },
                statusColorMode: userData.settings.statusColorMode,
            });
        }
    }, [open, isFetched, userData, form]);

    const { onError, onSubmit } = useUserSettings(userData, onClose);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={t('TOP_HEADER.USER_SETTINGS.SETTINGS.TITLE')}
            variant="sidebar"
            side="right"
            width="522px"
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit, onError)}>
                    <div className="flex flex-col gap-8">
                        <div className="flex flex-col gap-4">
                            <DisplaySettings disabled={!canChangePersonalSettings} />
                            <SmtpAccess disabled={!canChangeSmtp} outlined />
                            <SMimeCert disabled={!canChangeSmtp} outlined />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                iconLeft={<LuCircleX size={14} />}
                                onClick={onClose}
                            >
                                {t('BUTTONS.CANCEL')}
                            </Button>
                            <Button type="submit" variant="primary" disabled={disabled} iconLeft={<LuSave size={15} />}>
                                {t('BUTTONS.SAVE')}
                            </Button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </Modal>
    );
};
